import { decimalToUnits, MoneyError, unitsToDecimal } from './money.js';
/** An editable, ungrouped fiat amount. Never interpret pasted grouping as a decimal. */
export function createAmountInputCodec(locale, decimals) {
    // Validate the same precision domain used by all SDK money conversions.
    unitsToDecimal(0n, decimals);
    const number = new Intl.NumberFormat(locale, { useGrouping: false });
    const decimalSeparator = number.formatToParts(1.1).find((part) => part.type === 'decimal')?.value ?? '.';
    const digits = Array.from({ length: 10 }, (_, digit) => number.format(digit));
    return {
        decimalSeparator,
        parse(text, maximum) {
            if (text.length === 0 || text.length > 128)
                throw new MoneyError('invalid_amount');
            let canonical = '';
            for (const character of text) {
                const digit = digits.indexOf(character);
                if (digit >= 0)
                    canonical += digit.toString();
                else if (/^[0-9]$/.test(character))
                    canonical += character;
                else if (character === decimalSeparator)
                    canonical += '.';
                else
                    throw new MoneyError('invalid_amount');
            }
            if (!/^\d+(\.\d*)?$/.test(canonical))
                throw new MoneyError('invalid_amount');
            if (decimals === 0 && canonical.includes('.'))
                throw new MoneyError('precision_loss');
            canonical = canonical.replace(/^0+(?=\d)/, '').replace(/\.$/, '');
            return decimalToUnits(canonical, decimals, maximum);
        },
        key(text, key) {
            if (key === 'delete')
                return Array.from(text).slice(0, -1).join('');
            if (key === 'decimal')
                return decimals === 0 || text.includes(decimalSeparator)
                    ? text
                    : `${text || digits[0]}${decimalSeparator}`;
            if (!/^[0-9]$/.test(key))
                throw new MoneyError('invalid_amount');
            const digit = digits[Number(key)] ?? key;
            return text === '0' || text === digits[0] ? digit : text + digit;
        },
        format(units) {
            return unitsToDecimal(units, decimals)
                .replace(/(\.\d*?)0+$/, '$1')
                .replace(/\.$/, '')
                .split('')
                .map((character) => character === '.' ? decimalSeparator : (digits[Number(character)] ?? character))
                .join('');
        },
    };
}
